import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const EngagementTitleSlide = ({ eyebrow, titleStart, titleAccent, subtitle }) => {
  return (
    <div className="h-full w-full relative overflow-hidden flex flex-col items-center justify-center font-sans"
      style={{
        background:
          "radial-gradient(1200px 700px at 50% -10%, rgba(37,99,235,0.25) 0%, rgba(15,23,42,0) 60%), linear-gradient(180deg, #020617 0%, #0b1224 40%, #0a1128 100%)",
      }}
    >
      {/* ==== Animated ambient blur orbs ==== */}
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 680,
          height: 680,
          top: "-200px",
          left: "-120px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.35) 0%, rgba(59,130,246,0) 65%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 620,
          height: 620,
          bottom: "-220px",
          right: "-140px",
          background:
            "radial-gradient(circle, rgba(99,102,241,0.3) 0%, rgba(99,102,241,0) 65%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, -35, 0], y: [0, -25, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 420,
          height: 420,
          top: "30%",
          right: "-60px",
          background:
            "radial-gradient(circle, rgba(56,189,248,0.22) 0%, rgba(56,189,248,0) 65%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* ==== Grid mesh ==== */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.22) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 85%)",
        }}
      />

      {/* ==== Dot lattice ==== */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: "radial-gradient(rgba(148,163,184,0.35) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 80%)",
        }}
      />

      {/* ==== Floating particles ==== */}
      {[
        { left: "12%", top: "22%", delay: 0, size: 4 },
        { left: "85%", top: "18%", delay: 1.5, size: 3 },
        { left: "78%", top: "72%", delay: 2.5, size: 5 },
        { left: "18%", top: "78%", delay: 1, size: 3 },
        { left: "50%", top: "14%", delay: 3, size: 2 },
        { left: "8%", top: "52%", delay: 0.8, size: 3 },
        { left: "92%", top: "45%", delay: 2, size: 4 },
        { left: "34%", top: "88%", delay: 3.5, size: 2 },
        { left: "66%", top: "32%", delay: 1.8, size: 2 },
      ].map((p, i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute rounded-full bg-blue-300 pointer-events-none"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            boxShadow: "0 0 12px rgba(147,197,253,0.9)",
          }}
          animate={{
            opacity: [0.2, 0.9, 0.2],
            y: [0, -16, 0],
          }}
          transition={{
            duration: 4 + (i % 3),
            repeat: Infinity,
            ease: "easeInOut",
            delay: p.delay,
          }}
        />
      ))}

      {/* ==== Diagonal sweep lines ==== */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(110deg, transparent 0%, transparent 42%, rgba(147,197,253,0.08) 50%, transparent 58%, transparent 100%)",
        }}
        animate={{ x: ["-20%", "120%"] }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      />

      {/* ==== Corner accents ==== */}
      <div aria-hidden className="absolute top-8 left-8 w-20 h-20 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-blue-400/60 to-transparent" />
        <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-blue-400/60 to-transparent" />
      </div>
      <div aria-hidden className="absolute top-8 right-8 w-20 h-20 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-px bg-gradient-to-l from-blue-400/60 to-transparent" />
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-blue-400/60 to-transparent" />
      </div>
      <div aria-hidden className="absolute bottom-8 left-8 w-20 h-20 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-blue-400/60 to-transparent" />
        <div className="absolute bottom-0 left-0 w-px h-full bg-gradient-to-t from-blue-400/60 to-transparent" />
      </div>
      <div aria-hidden className="absolute bottom-8 right-8 w-20 h-20 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-blue-400/60 to-transparent" />
        <div className="absolute bottom-0 right-0 w-px h-full bg-gradient-to-t from-blue-400/60 to-transparent" />
      </div>

      {/* ==== Content ==== */}
      <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-5xl">
        {/* Icon with rotating ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative mb-7"
        >
          {/* Outer rotating ring */}
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              width: 120,
              height: 120,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              background:
                "conic-gradient(from 0deg, rgba(59,130,246,0) 0%, rgba(59,130,246,0.8) 25%, rgba(99,102,241,0.8) 50%, rgba(59,130,246,0) 75%, rgba(59,130,246,0) 100%)",
              mask: "radial-gradient(circle, transparent 48px, black 49px, black 58px, transparent 59px)",
              WebkitMask: "radial-gradient(circle, transparent 48px, black 49px, black 58px, transparent 59px)",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
          {/* Inner rotating ring, reverse direction */}
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              width: 100,
              height: 100,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              background:
                "conic-gradient(from 180deg, rgba(147,197,253,0) 0%, rgba(147,197,253,0.4) 30%, rgba(147,197,253,0) 60%, rgba(147,197,253,0) 100%)",
              mask: "radial-gradient(circle, transparent 40px, black 41px, black 48px, transparent 49px)",
              WebkitMask: "radial-gradient(circle, transparent 40px, black 41px, black 48px, transparent 49px)",
            }}
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          />
          {/* Pulsing glow */}
          <motion.div
            aria-hidden
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              background:
                "radial-gradient(circle, rgba(59,130,246,0.55) 0%, rgba(59,130,246,0) 70%)",
              filter: "blur(14px)",
            }}
            animate={{ opacity: [0.5, 1, 0.5], scale: [0.9, 1.15, 0.9] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Icon container */}
          <div
            className="relative w-20 h-20 rounded-2xl flex items-center justify-center border border-blue-400/30 backdrop-blur-sm shadow-[0_8px_40px_-8px_rgba(59,130,246,0.6)]"
            style={{
              background:
                "linear-gradient(135deg, rgba(59,130,246,0.25) 0%, rgba(99,102,241,0.2) 100%)",
            }}
          >
            <Sparkles className="w-9 h-9 text-blue-300" />
          </div>
        </motion.div>

        {/* Eyebrow with side bars */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex items-center gap-4 mb-6"
        >
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-blue-400/70" />
          <p className="text-[12px] font-bold text-blue-300 uppercase tracking-[0.4em]">
            {eyebrow}
          </p>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-blue-400/70" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="text-5xl md:text-7xl lg:text-[88px] font-bold tracking-tight text-white leading-[1.05]"
        >
          {titleStart}{" "}
          <span className="relative inline-block">
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #60a5fa 0%, #818cf8 50%, #60a5fa 100%)",
                backgroundSize: "200% 100%",
              }}
            >
              <motion.span
                className="inline-block bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #60a5fa 0%, #a5b4fc 50%, #60a5fa 100%)",
                  backgroundSize: "200% 100%",
                }}
                animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
              >
                {titleAccent}
              </motion.span>
            </span>
            <motion.span
              aria-hidden
              className="absolute left-0 right-0 -bottom-2 h-[3px] rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(96,165,250,0.7), rgba(129,140,248,0.7), transparent)",
              }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.8, ease: "easeOut" }}
            />
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="text-lg md:text-xl text-slate-300/90 max-w-2xl mx-auto mt-7 leading-relaxed font-light"
        >
          {subtitle}
        </motion.p>

        {/* Bottom progress dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.65 }}
          className="flex items-center gap-2 mt-10"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.2, 0.9] }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.3,
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* ==== Bottom gradient fade ==== */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(2,6,23,0.6) 100%)",
        }}
      />
    </div>
  );
};

export default EngagementTitleSlide;
